import { createContext, useContext, useEffect, useMemo, useState } from "react";

const ShopContext = createContext(null);
export const useShop = () => useContext(ShopContext);

const CART_KEY = "sashti_cart_v1";
const THEME_KEY = "sashti_theme_v1";

export function ShopProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CART_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [enquiry, setEnquiry] = useState({ open: false, product: null });
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || "light");

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const addToCart = (product, qty = 1) => {
    setCart((c) => {
      const found = c.find((i) => i.id === product._id);
      if (found) {
        return c.map((i) =>
          i.id === product._id ? { ...i, qty: Math.min(10, i.qty + qty) } : i
        );
      }
      return [
        ...c,
        {
          id: product._id,
          name: product.name,
          price: product.price,
          image: (product.images && product.images[0]) || "",
          category: product.category,
          qty,
        },
      ];
    });
  };

  const updateQty = (id, qty) =>
    setCart((c) => c.map((i) => (i.id === id ? { ...i, qty: Math.max(1, Math.min(10, qty)) } : i)));

  const removeFromCart = (id) => setCart((c) => c.filter((i) => i.id !== id));
  const clearCart = () => setCart([]);

  const openEnquiry = (product = null) => setEnquiry({ open: true, product });
  const closeEnquiry = () => setEnquiry({ open: false, product: null });

  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

  const count = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.qty * i.price, 0), [cart]);

  return (
    <ShopContext.Provider
      value={{
        cart,
        addToCart,
        updateQty,
        removeFromCart,
        clearCart,
        cartOpen,
        setCartOpen,
        count,
        subtotal,
        theme,
        toggleTheme,
        enquiry,
        openEnquiry,
        closeEnquiry,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
}
